import { useState, useEffect, useCallback } from 'react';
import { supabase, type Product } from '../lib/supabase';

const STORAGE_KEY = 'affilix_checked_products';

function getStoredIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as string[];
  } catch {
    return [];
  }
}

function storeIds(ids: string[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
}

export function useCheckedProducts() {
  const [checkedIds, setCheckedIds] = useState<string[]>(getStoredIds);
  const [checkedProducts, setCheckedProducts] = useState<Product[]>([]);

  // Fetch checked products from database
  useEffect(() => {
    const fetchCheckedProducts = async () => {
      if (checkedIds.length === 0) {
        setCheckedProducts([]);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .in('id', checkedIds);

        if (error) throw error;
        setCheckedProducts(data || []);
      } catch (error) {
        console.error('Error fetching checked products:', error);
      }
    };

    fetchCheckedProducts();
  }, [checkedIds]);

  useEffect(() => {
    storeIds(checkedIds);
  }, [checkedIds]);

  const toggleCheck = useCallback((id: string) => {
    setCheckedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(i => i !== id);
      }
      return [...prev, id];
    });
  }, []);

  const isChecked = useCallback((id: string) => {
    return checkedIds.includes(id);
  }, [checkedIds]);

  const clearAll = useCallback(() => {
    setCheckedIds([]);
  }, []);

  return {
    checkedIds,
    checkedProducts,
    toggleCheck,
    isChecked,
    clearAll,
    count: checkedIds.length,
  };
}
